import React, {useMemo} from "react";
import {observer} from "mobx-react-lite";

import appStore from "../storage/AppStore";

import Input from "./Input";
import CheckBox from "./CheckBox";
import ClickableMap from "./ClickableMap";
import Btn from "./Btn";


const DEFAULT_LAT = 42.876760;
const DEFAULT_LNG = 74.602019;


const toCoordinate = (value) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return null;
    }

    const number = Number(value);

    return Number.isFinite(number)
        ? number
        : null;
};


const normalizePoint = (value) => {
    const lat =
        toCoordinate(value?.lat);

    const lng =
        toCoordinate(value?.lng);


    if (
        lat === null ||
        lng === null ||
        lat < -90 ||
        lat > 90 ||
        lng < -180 ||
        lng > 180
    ) {
        return null;
    }


    return {
        lat,
        lng,
    };
};


const Location = ({
    store,
    location,
}) => {

    const center = useMemo(() => {
        /*
         * Selected point has priority.
         */
        const point =
            normalizePoint(
                location.location
            );


        if (point) {
            return point;
        }


        /*
         * User coordinates are used only
         * as the initial visual map center.
         * They are NOT written into location.location.
         */
        const userLat =
            toCoordinate(
                appStore.user?.lat
            );

        const userLng =
            toCoordinate(
                appStore.user?.lon
            ) ??
            toCoordinate(
                appStore.user?.lng
            );


        return {
            lat:
                userLat !== null &&
                userLat >= -90 &&
                userLat <= 90
                    ? userLat
                    : DEFAULT_LAT,

            lng:
                userLng !== null &&
                userLng >= -180 &&
                userLng <= 180
                    ? userLng
                    : DEFAULT_LNG,
        };

    }, [
        location.location?.lat,
        location.location?.lng,

        appStore.user?.lat,
        appStore.user?.lon,
        appStore.user?.lng,
    ]);


    const setMain = () => {
        (store.locations || []).forEach(
            (item) => {
                const selected =
                    item === location;


                if (
                    typeof item.setProperty ===
                    "function"
                ) {
                    item.setProperty(
                        "is_main",
                        selected
                    );
                } else {
                    item.is_main =
                        selected;
                }
            }
        );
    };


    const handleMainChange = (
        checked
    ) => {
        /*
         * Main location cannot simply be
         * unchecked. Another location must
         * be selected as main instead.
         */
        if (checked) {
            setMain();
        }
    };


    const handleDelete = () => {
        store.deleteLocation?.(
            location.id
        );
    };


    const canDelete =
        (store.locations?.length || 0) > 1;


    return (
        <div className="secondary-card mb-3">
            <div className="grid align-items-end">

                {/* MAIN LOCATION */}

                <div className="col-12 md:col-2">
                    <CheckBox
                        checked={
                            Boolean(
                                location.is_main
                            )
                        }

                        label={
                            appStore.tr.main_location
                        }

                        onChange={
                            handleMainChange
                        }
                    />
                </div>


                {/* NAME */}

                <div className="col-12 md:col-4">
                    <label className="block mb-1">
                        {appStore.tr.name}
                    </label>

                    <Input
                        store={location}
                        prop="name"
                    />
                </div>


                {/* ADDRESS */}

                <div className="col-12 md:col-5">
                    <label className="block mb-1">
                        {appStore.tr.address}
                    </label>

                    <Input
                        store={location}
                        prop="address"
                    />
                </div>


                {/* DELETE */}

                <div className="col-12 md:col-1 flex md:justify-content-end">
                    <Btn
                        icon="pi pi-trash"

                        severity="danger"
                        outlined

                        disabled={
                            !canDelete
                        }

                        tooltip={
                            canDelete
                                ? appStore.tr.delete
                                : undefined
                        }

                        aria-label={
                            appStore.tr.delete ||
                            "Delete location"
                        }

                        onClick={
                            handleDelete
                        }
                    />
                </div>


                {/* MAP */}

                <div className="col-12">
                    <ClickableMap
                        store={location}
                        prop="location"

                        center={center}

                        height={280}
                    />
                </div>

            </div>
        </div>
    );
};


export default observer(Location);